import { OTHER_CKBX_VAL } from "./OtherCheckboxUtils";

//a few access methods to play with, pretty close to how they might come in from the database
//(not all properties are included, though, just the ones I need to get going)
const imagePath = '/images/access-methods/';
export const AM_CATEGORIES = {
  POINTING_DEVICE: 'Pointing devices',
  EYEGAZE_ACCESS: 'Eye-gaze access',
  SPEECH_RECOGNITION: 'Speech recognition',
  KEYBOARD: 'Keyboards',
  TOUCHSCREEN: 'Touchscreen access',
  NO_TECH: 'No-tech access',
  SWITCH_ACCESS: 'Switch access'
};
//an iterable version of the categories
export const CAT_ARRAY = [
  AM_CATEGORIES.KEYBOARD,
  AM_CATEGORIES.TOUCHSCREEN,
  AM_CATEGORIES.SPEECH_RECOGNITION, 
  AM_CATEGORIES.EYEGAZE_ACCESS,
  AM_CATEGORIES.POINTING_DEVICE,
  AM_CATEGORIES.NO_TECH,
  AM_CATEGORIES.SWITCH_ACCESS
];
export const LOWTECH_CAT_ARRAY = [
  AM_CATEGORIES.NO_TECH,
  AM_CATEGORIES.SWITCH_ACCESS
];

export const SELECTION_METHODS = {
  BUTTON: 'built-in-button',
  DWELL: 'dwell',
  SWITCH: 'switch',
  TAP: 'tap',
  SMILE: 'smile',
  BLINK: 'blink',
  SPEECH: 'speech',
  DIRECT: 'direct',
  SCAN: 'scanning',
  MORSE: 'morse'
}
export const LOWTECH_SELECTION_METHODS = {
  DIRECT: 'direct-notech',
  PSCAN: 'partner-assist-scan'
}
/*
longDesc: ``,
textEntryItems: [
    ''
],
navigationItems: [
  ''
],
motorConsiderations: [
  ``
],
visualConsiderations: [
  ``
],
cognitiveConsiderations: [
  ``
],
setupConsiderations: [
  ``
],
productConsiderations: [
  `For a laptop or desktop computer, the conventional keyboard is supplied with the device.`,
  `There are after-market keyboards available as well.`,
  `For a tablet, there are physical keyboards available that connect to the tablet and may support a better typing experience for some users. These can also allow the keyboard and the display to be separated, for more flexibility in positioning.`  
]
*/

/* Use a 4:3 ratio for the images here */
export const ACCESS_METHODS = [ 
  {
    id: 'eyetracking',
    name: 'Eye tracking',
    category: AM_CATEGORIES.EYEGAZE_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
      SELECTION_METHODS.BLINK,
    ],
    imgSrc: imagePath+'eye-access-240x180-opt.png',
    imgAlt: 'eye tracking',
    subTitle: '',
    shortDesc: 'Eye movements plus dwell or switch to select',
    longDesc: `Eye tracking uses a special sensor to track where your eyes are looking. To select an item like a button, first rest your gaze on the item. Then select it by pressing a switch. Or use dwell selection instead of a switch press.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
    ],
    navigationItems: [
      'Eyetracking can be used for navigation but it can be difficult to select very precise targets.'
    ],
    motorConsiderations: [
      `Good eye movements generally required`,
    ],
    visualConsiderations: [
      `Eyeglasses may impact calibration - adjust lighting and positioning if this is a problem`,
      `Dry eyes can decrease accuracy - eye drops can help`,
      `Eyetracking can be adjusted to accommodate some vision and occulomotor issues`,
      `Calibration may allow you to use one eye instead of both`,
      `Some eye trackers have software that can accommodate for eye droop`
    ],
    cognitiveConsiderations: [
      `For young children, cause/effect of using eyes to select content on a display may take time and practice to develop`,
    ],
    setupConsiderations: [
      `Software for calibration, typing, navigation, and communication generally comes with the eyetracker or is built into the SGD`,
      `Check out the settings provided by the included software`,
      `Eyetracker positioning: 18-24" away, face parallel with eyetracker. Calibration process may give additional guidance.`,
      `All eyetrackers are not the same. May differ in compatibility, tracking precision, and selection method.`,
      `Outdoor use may be suboptimal - test this out if needed`
    ],
    productConsiderations: []

  },
  {
    id: 'regular-mouse',
    name: 'Mouse',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'mouse-unsplash-1024x768-opt.png',
    imgAlt: 'mouse',
    subTitle: '',
    shortDesc: 'Regular mouse for point and click',
    longDesc: `This is the conventional mouse that has been around for years. Moving the mouse controls the mouse cursor, and buttons on the mouse allow clicking and scrolling.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Mice are well-suited for navigation and precise targetting, for people who can control it precisely.'
    ],
    motorConsiderations: [
      `Typical access point is the hand, and fingers for the buttons. Usually requires ability to reposition the mouse by picking it up and placing it down again.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `Dwell selection is possible (may require extra software)`
    ],
    productConsiderations: []

  },
  {
    id: 'trackpad',
    name: 'Trackpad',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.TAP,
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'trackpad-unsplash-1024x768-opt.png',
    imgAlt: 'trackpad',
    subTitle: '',
    shortDesc: 'Basic trackpad as a mouse alternative',
    longDesc: `The trackpad is a mainstream pointing device that is built-in to most laptops and some keyboards. To move the mouse cursor, move your fingers on the pad. Use swipes and other gestures for actions like scroll and zoom. Click using a tap or physical buttons. Or use dwell selection instead of button presses (usually requires extra software).`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Trackpads are well-suited for navigation and precise targetting, with typical finger control.'
    ],
    motorConsiderations: [
      `Typical access points are the fingers and hand. This generally requires ability to lift your hand on/off the trackpad without moving the cursor.`,
      `Less common access points include using a knuckle, mouthstick, or toes.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `control accessories include splints, cuffs, digit isolation glove, stylus, mouthstick`,
      `External trackpads are available, allowing you to position the trackpad in the best location`,
    ],
    productConsiderations: []

  },
  {
    id: 'trackball',
    name: 'Trackball',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'trackball-wikimedia-1024x768-opt.png',
    imgAlt: 'trackball',
    attr: `Photo by Bobulous, via Wikimedia Commons`,
    subTitle: '',
    shortDesc: 'Basic trackball as a mouse alternative',
    longDesc: `The trackball is a mouse replacement that some people find easier to use. To move the mouse cursor, roll the ball. Click using buttons on the trackball, or plug in a separate switch (switch jack may not be included). Or use dwell selection instead of button presses (usually requires extra software).`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Trackballs are well-suited for navigation and precise targetting, with good control of the ball.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to get your hand on/off the ball without moving it unintentionally.`,
      `Less common access points include using a mouthstick, chin, or foot.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `control accessories include splints, cuffs, dycem non-slip material`,
      `for trackball position, consider: lateral vs midline, laptray vs specialized mount, relationship to keyboard`,
      `If using switches to click: consider best location and how to put and keep the switches there`
    ],
    productConsiderations: []
  },
  {
    id: 'thumb-trackball',
    name: 'Thumb trackball',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'thumb-trackball-wikimedia-1024x768-opt.png',
    imgAlt: 'thumb trackball',
    subTitle: '',
    shortDesc: 'Trackball designed for use with thumb',
    longDesc: `The thumb trackball is a mouse replacement -- similar to a regular trackball except that the ball is mounted on the side and you roll it with your thumb. Click using buttons on the trackball, or plug in a separate switch (switch jack may not be included). Or use dwell selection instead of button presses (usually requires extra software).`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Trackballs are well-suited for navigation and precise targetting, with good control of the ball.'
    ],
    motorConsiderations: [
      `Typical access points are the thumb and hand. This generally requires ability to get your hand on/off the ball without moving the mouse cursor.`,
      `Less common access points include using the chin, although consider head controls in that situation as well.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `control accessories include splints, cuffs, dycem non-slip material`,
      `for trackball position, consider: lateral vs midline, laptray vs specialized mount, relationship to keyboard`,
      `If using switches to click: consider best location and how to put and keep the switches there`
    ],
    productConsiderations: []
  },
  {
    id: 'huge-trackball',
    name: 'Large trackball',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'big-trackball-880x660-opt.png',
    imgAlt: 'huge trackball',
    attr: 'Photo by permission of <a href="https://www.ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: '',
    shortDesc: 'Extra large trackball as a mouse alternative',
    longDesc: `This is a trackball with an oversized ball. It's a mouse replacement, just like a regular trackball, but some people find a larger ball easier to use. To move the mouse cursor, roll the ball. Click using buttons on the trackball, or plug in a separate switch (switch jack may not be included). Or use dwell selection instead of button presses (usually requires extra software).`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Trackballs are well-suited for navigation and precise targetting, with good control of the ball.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to get your hand on/off the ball without moving it unintentionally.`,
      `Less common access points include using a mouthstick or foot.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `control accessories include splints, cuffs, dycem non-slip material`,
      `for trackball position, consider: lateral vs midline, laptray vs specialized mount, relationship to keyboard`,
      `If using switches to click: consider best location and how to put and keep the switches there`
    ],
    productConsiderations: []

  },
  {
    id: 'switch-adapted-mouse',
    name: 'Switch-adapted mouse',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'switch-adapted-mouse-600x450-opt.png',
    imgAlt: 'switch-adapted mouse',
    attr: 'Photo by permission of <a href="https://enablingdevices.com/" target="_blank">Enabling Devices</a>',
    subTitle: '',
    shortDesc: 'Regular mouse with switch jacks for clicking',
    longDesc: `This is a conventional mouse that also includes switch jacks for clicking. One or two external switches can be connected and used for left- and right-click. This may make it easier to click without inadvertently moving the mouse cursor.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Mice are well-suited for navigation and precise targetting, for people who can control it precisely.'
    ],
    motorConsiderations: [
      `Typical access point is the hand, for moving the mouse. Usually requires ability to reposition the mouse by picking it up and placing it down again.`,
      `Can accommodate users who can move the mouse but has difficulty using the typical buttons for clicking`,
      `Use external switches with any appropriate body site, such as the non-mouse hand, head, or sip/puff.`
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `For switch position: consider best location and how to put and keep the switch there`
    ],
    productConsiderations: []
  },
  {
    id: 'joystick-mouse',
    name: 'Joystick mouse',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'joystick-mouse-640x480-opt.png',
    imgAlt: 'joystick mouse',
    attr: 'Photo by permission of <a href="https://www.pretorianuk.com/" target="_blank">Pretorian UK</a>',
    subTitle: '',
    shortDesc: 'Joystick mouse, typically used when other pointing devices aren\'t suitable',
    longDesc: `These are joysticks designed to replace a mouse, typically used when other pointing devices aren't suitable. Some are designed mainly for hand control, and others are mainly for lip / chin control. Click using buttons on the joystick, or plug in a separate switch (switch jack may not be included). Or use dwell selection instead of button presses (usually requires extra software).`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Joysticks are well-suited for navigation and precise targetting, for people who can control it precisely.'
    ],
    motorConsiderations: [
      `Typical access point is the hand, for moving the mouse cursor. Usually requires ability to get hand on / off joystick without moving mouse cursor.`,
      `A less typical access point is the lips or chin, usually with joysticks designed for that purpose.`,
      `Use external switches with any appropriate body site, such as the non-mouse hand, head, or sip/puff.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `Alternative handles (T-handle, goal post, larger ball) may help with control.`,
      `for joystick position, consider: lateral vs midline, laptray vs specialized mount, relationship to keyboard`,
      `If using switches to click: consider best location and how to put and keep the switches there`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],

  },
  {
    id: 'dot-tracker',
    name: 'Head tracker',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'head-mouse-nano-600x450-opt.png',
    imgAlt: 'Dot tracker mouse',
    attr: 'Photo by permission of <a href="https://orin.com/" target="_blank">Origin Instruments</a>',
    subTitle: '',
    shortDesc: 'User-worn dot tracked by sensor unit',
    longDesc: `This is a mouse replacement that uses a reflective dot worn on the forehead and a sensor unit that tracks light reflected off the dot. To move the mouse cursor, move your head left/right and up/down. To click, use external switches for left- and right-click, or dwell selection.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Head-controlled dot trackers are well-suited for navigation and precise targetting, for people with good head control.'
    ],
    motorConsiderations: [
      `Typical access point is the head, for moving the mouse.`,
      `can also put the reflective dot on another well-controlled body part, such as the hand`,
      `Need to hold head steady while clicking, whether using external switches or dwell selection`,
      `Use external switches with any appropriate body site, such as the hand or sip/puff.`,
    ],
    visualConsiderations: [
      `Need to be able to track mouse cursor visually while moving head`,
      `Unaddressed double vision, binocularity deficits, occulmotor defices may present challenges`
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `If using switches to click: consider best location and how to put and keep the switches there`,
      `See <a href="https://kpronline.com/blog/your-guide-to-25-hands-free-mice/" target="_blank">our blog post</a> for more about hands-free mice`
    ],
    productConsiderations: []

  },
  {
    id: 'wearable-sensor-mouse',
    name: 'Wearable sensor mouse',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'glassouse-280x210-opt.png',
    imgAlt: 'Wearable sensor mouse',
    attr: 'Photo by permission of <a href="https://www.glassouse.com/" target="_blank">Glassouse</a>',
    subTitle: '',
    shortDesc: 'User-worn sensor (typically on head)',
    longDesc: `This is a mouse replacement that uses a wearable sensor to control the mouse cursor. To move the mouse cursor, move your head left/right and up/down - no direct line-of-sight required. To click, use external switches for left- and right-click, or dwell selection.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Head-controlled wearable sensors are well-suited for navigation and precise targetting, for people with good head control.'
    ],
    motorConsiderations: [
      `Typical access point is the head, for moving the mouse.`,
      `May also be able to wear the sensor on another well-controlled body part, such as the hand`,
      `Need to hold head steady while clicking, whether using external switches or dwell selection`,
      `Use external switches with any appropriate body site, such as the hand or sip/puff.`,
    ],
    visualConsiderations: [
      `Need to be able to track mouse cursor visually while moving head`,
      `Unaddressed double vision, binocularity deficits, occulmotor defices may present challenges`
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `If using switches to click: consider best location and how to put and keep the switches there`,
      `See <a href="https://kpronline.com/blog/your-guide-to-25-hands-free-mice/" target="_blank">our blog post</a> for more about hands-free mice`
    ],
    productConsiderations: []
  },
  {
    id: 'face-tracker',
    name: 'Face tracker',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
      SELECTION_METHODS.SMILE
    ],
    imgSrc: imagePath+'smyle-mouse-272x204-opt.png',
    imgAlt: 'face tracker mouse',
    attr: 'Photo by permission of <a href="https://SmyleMouse.com/" target="_blank">Smyle Mouse</a>',
    subTitle: '',
    shortDesc: 'Facial features tracked by webcam',
    longDesc: `This is a mouse replacement that uses the built-in camera to track your face motion. To move the mouse cursor, rotate your head left/right and up/down. To click, use facial gestures, dwell selection, or external switches.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      `Basic targetting and navigation may work fine.`,
      `Selecting precise targets can be difficult.`
    ],
    motorConsiderations: [
      `Typical access point is the head, for moving the mouse.`,
      `Need to hold head steady while clicking, whether using facial gestures, dwell selection, or external switches.`,
      `Use external switches with any appropriate body site, such as the hand or sip/puff.`,
    ],
    visualConsiderations: [
      `Need to be able to track mouse cursor visually while moving head`,
      `Unaddressed double vision, binocularity deficits, occulmotor defices may present challenges`,
      `Need to be able to see and understand the selection options after a click is initiated.`
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the settings included with face tracking feature`,
      `If using switches to click: will need a separate switch interface or switch-adapted mouse. Consider best location and how to put and keep the switches there`,
      `See <a href="https://kpronline.com/blog/your-guide-to-25-hands-free-mice/" target="_blank">our blog post</a> for more about hands-free mice`
    ],
    productConsiderations: []
  },

  {
    id: 'other-pointing-device',
    name: 'Other pointing device',
    category: AM_CATEGORIES.POINTING_DEVICE,
    selectionMethod: [
      SELECTION_METHODS.BUTTON,
      SELECTION_METHODS.SWITCH,
      SELECTION_METHODS.DWELL, 
    ],
    imgSrc: imagePath+'other-pointing-devices-istock-1024x768-opt.png',
    imgAlt: 'other pointing device',
    attr: 'Rollermouse photo by permission of <a href="https://contourdesign.com/" target="_blank">Contour Design</a>',
    subTitle: 'RollerMouse, vertical mouse, foot mouse, and others',
    shortDesc: '',
    longDesc: `This category is for any type of pointing device that doesn't fit in our other categories. For example, a RollerMouse allows you to move the mouse cursor using a rollerbar at your fingertips. 
    A vertical mouse places your hand in a neutral 'handshake' position. A foot mouse is operated with the feet. As with the other access methods, you can enter a specific product name when you name the setup.`,
    textEntryItems: [
        'Text entry is possible, with an on-screen keyboard, but usually fairly slow.',
        'Most computing platforms include an on-screen keyboard, or you can get a third party one.'
    ],
    navigationItems: [
      'Most products in this category will have the precise navigation and targetting of a regular mouse, if the user can control the device readily.'
    ],
    motorConsiderations: [
      `The access point will vary depending on the details of the pointing device.`,
    ],
    visualConsiderations: [
      ``
    ],
    cognitiveConsiderations: [
      `Dwell selection can be a challenge, as it may require a two-step process, where you first choose the action you want, then you dwell on an item to perform that action.`,
    ],
    setupConsiderations: [
      `Check out the mouse accessibility features included with the computing platform`,
      `Dwell selection is possible (may require extra software)`
    ],
    productConsiderations: []

  },
  {
    id: 'regular-keyboard',
    name: 'Conventional keyboard',
    category: AM_CATEGORIES.KEYBOARD,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'conventional-keyboard-wikimedia-1024x768-opt.png',
    imgAlt: 'conventional keyboard',
    subTitle: '',
    shortDesc: "Regular keyboard on laptop or desktop computer",
    longDesc: `This includes the regular keyboard on a laptop or desktop computer, as well as a physical keyboard connected to a tablet.`,
    textEntryItems: [
        'Keyboards are best-suited for text entry (typing!).'
    ],
    navigationItems: [
      'They can be used for navigation using keystroke commands, tabbing, cursor keys, and the mouse keys accessibility feature.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the keyboard, or the use of stabilization or positioning support.`,
      `Other access points for keyboard typing include head control (with a mouth or headstick), as well as toes.`,
    ],
    visualConsiderations: [
      `Generally requires ability to see the keys (larger font stickers or black/yellow keyboards are available to accommodate low vision).`,
      `Looking from the keyboard to the display can be an issue for those who 'hunt and peck' or are single-finger typists.`,
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the keyboard accessibility features included with the computing platform`,
      `Control accessories include typing sticks, splints, mouthsticks, and keyguards`,
      `For keyboard position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []
  },
  {
    id: 'large-key-keyboard',
    name: 'Large key keyboard',
    category: AM_CATEGORIES.KEYBOARD,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'pretorian-keyboard-1024x768-opt.png',
    imgAlt: 'large-key keyboard',
    attr: 'Photo by permission of <a href="https://www.pretorianuk.com/" target="_blank">Pretorian UK</a>',
    subTitle: '',
    shortDesc: "Keyboard with larger keys requiring less precise control",
    longDesc: `These are full-featured keyboards with oversized keys, about 1" square. May be a good fit for those who need larger targets due to motor or visual issues.`,
    textEntryItems: [
        'Keyboards are best-suited for text entry (typing!).'
    ],
    navigationItems: [
      'They can be used for navigation using keystroke commands, tabbing, cursor keys, and the mouse keys accessibility feature.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the keyboard, or the use of stabilization or positioning support.`,
      `Other access points for keyboard typing include head control (with a mouth or headstick), as well as toes.`,
    ],
    visualConsiderations: [
      `Larger keys may be easier to see. Black on yellow keys are also available which helps visibility for some people.`,
      `Looking from the keyboard to the display can be an issue for those who 'hunt and peck' or are single-finger typists.`,
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the keyboard accessibility features included with the computing platform`,
      `Some large key keyboards do not allow auto-repeat when holding down keys`,
      `Connect to tablet with adapter`,
      `Control accessories include typing sticks, splints, mouthsticks, and keyguards`,
      `For keyboard position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []
  },
  {
    id: 'compact-keyboard',
    name: 'Compact keyboard',
    category: AM_CATEGORIES.KEYBOARD,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'compact-keyboard-pd-1024x768-opt.png',
    imgAlt: 'compact keyboard',
    subTitle: '',
    shortDesc: "A smaller keyboard requiring less movement",
    longDesc: `A full-featured keyboard with smaller keys and smaller overall size. May be a good fit for those who have precise fine motor control but limited range. Also may be good for portability.`,
    textEntryItems: [
        'Keyboards are best-suited for text entry (typing!).'
    ],
    navigationItems: [
      'They can be used for navigation using keystroke commands, tabbing, cursor keys, and the mouse keys accessibility feature.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the keyboard, or the use of stabilization or positioning support.`,
      `Other access points for keyboard typing include head control (with a mouth or headstick), as well as toes.`,
    ],
    visualConsiderations: [
      `Smaller keys may be harder to see.`,
      `Looking from the keyboard to the display can be an issue for those who 'hunt and peck' or are single-finger typists.`,
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the keyboard accessibility features included with the computing platform`,
      `Control accessories include typing sticks, splints, mouthsticks, and keyguards`,
      `For keyboard position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []
  },
  {
    id: 'one-hand-keyboard',
    name: 'One-hand keyboard',
    category: AM_CATEGORIES.KEYBOARD,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'one-hand-keyboard-1024x768-opt.png',
    imgAlt: 'one-hand keyboard',
    subTitle: '',
    shortDesc: "Keyboard designed specifically for one-hand typing",
    longDesc: `These are typing options for people who have good dexterity in one hand but limited use of the other. Photo shows a specific example of a one-handed keyboard, from Maltron, but there other approaches available too. See <a href="https://kpronline.com/blog/your-guide-to-10-one-hand-typing-options/" target="_blank">our blog post</a> for some ideas and considerations.`,
    textEntryItems: [
        'Keyboards are best-suited for text entry (typing!).'
    ],
    navigationItems: [
      'They can be used for navigation using keystroke commands, tabbing, cursor keys, and the mouse keys accessibility feature.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the keyboard, or the use of stabilization or positioning support.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
      `Some one-hand keyboards require learning a specific keyboard layout or unusual motor pattern.`
    ],
    setupConsiderations: [
      `Check out the keyboard accessibility features included with the computing platform`,
      `For keyboard position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []
  },
  {
    id: 'other-keyboard',
    name: 'Other keyboard',
    category: AM_CATEGORIES.KEYBOARD,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'other-keyboards-1024x768-opt.png',
    imgAlt: 'other keyboard',
    attr: 'Photos by permission of <a href="https://kinesis.com/" target="_blank">Kinesis</a> and <a href="https://key2enable.ae/" target="_blank">Key2Enable</a>',
    subTitle: 'Ergonomic or other specialist keyboard',
    shortDesc: '',
    longDesc: `This category is for any type of keyboard that doesn't fit in our other categories. For example, a split keyboard places the hands in a more neutral alignment, and may allow for unique placement of each keyboard half. As with the other access methods, you can enter a specific product name when you name the setup.`,
    textEntryItems: [
        'Keyboards are best-suited for text entry (typing!).'
    ],
    navigationItems: [
      'They can be used for navigation using keystroke commands, tabbing, cursor keys, and the mouse keys accessibility feature.',
      'Some less common keyboards may have the cursor keys or command keys in unusual places. If you plan to use keyboard navigation, be sure to assess how readily the user can navigate with this specific keyboard.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the keyboard, or the use of stabilization or positioning support.`,
      `Other access points for keyboard typing include head control (with a mouth or headstick), as well as toes.`,
    ],
    visualConsiderations: [
      `Generally requires ability to see the keys (larger font stickers or black/yellow keyboards are available to accommodate low vision).`,
      `Looking from the keyboard to the display can be an issue for those who 'hunt and peck' or are single-finger typists.`,
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the keyboard accessibility features included with the computing platform`,
      `Control accessories include typing sticks, splints, mouthsticks, and keyguards`,
      `For keyboard position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []
  },

  {
    id: 'touchscreen',
    name: 'Touchscreen',
    category: AM_CATEGORIES.TOUCHSCREEN,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'touchscreen-unsplash-1024x768-opt.png',
    imgAlt: 'touchscreen',
    subTitle: '',
    shortDesc: "Regular touchscreen on tablet or phone",
    longDesc: `This includes the regular touchscreen on a tablet or phone, as well as a touchscreen on a speech-generating device (SGD).`,
    textEntryItems: [
        'Touchscreen typing uses an on-screen keyboard. People who can use both a touchscreen and a physical keyboard may be faster and more accurate on the physical keyboard.'
    ],
    navigationItems: [
      'Navigation using a touchscreen involves directly touching the button or item that you want. There may also be special gestures for zoom, drag, scroll, etc.'
    ],
    motorConsiderations: [
      `Typical access points are the finger and hand. This generally requires ability to lift arm to the touchscreen, or the use of stabilization or positioning support.`,
      `Other access points for touchscreen typing include head control (with a mouth or headstick), as well as toes.`,
    ],
    visualConsiderations: [
      `Typically relies on vision to see the letters and on-screen items, especially since there are no tactile markers on the screen`,
      `There may be accessibility features on the computing platofmr to aid people with vision impairments`
    ],
    cognitiveConsiderations: [
      `Dealing with auto-correct for text can be confusing to some`
    ],
    setupConsiderations: [
      `Check out the touch accessibility features included with the computing platform or SGD`,
      `control accessories include: splints, cuffs, stylus, digit isolation glove, mouthstick`,
      `For touchscreen position, consider: flat vs angled, lap vs laptray vs specialized mount`,
      `For overall positioning, consider: hi-lo desks, wheelchair accessible desks/tables, etc.`
    ],
    productConsiderations: []

  },
  {
    id: 'speech-recognition',
    name: 'Speech recognition',
    category: AM_CATEGORIES.SPEECH_RECOGNITION,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
    ],
    imgSrc: imagePath+'speech-recog-240x180-opt.png',
    imgAlt: 'speech recognition',
    subTitle: '',
    shortDesc: "Use speech to type and/or perform actions",
    longDesc: `This includes speech recognition that is built-in to the computing platform, as well as third-party speech recognition (like Dragon). Dictate to enter text, and speak commands to click buttons, edit text, open apps, etc.`,
    textEntryItems: [
        'Some users are very efficient, but not everyone reaches that level.',
        `Consider editing text in some way other than by voice, if possible.`
    ],
    navigationItems: [
      'Can navigate and do actions using voice commands.',
      'Selecting precise targets can be difficult or tedious.'
    ],
    motorConsiderations: [
      `Best results with clearly intelligible speech.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
      `High frustration tolerance, good memory, and good problem solving skills are recommended, especially if using speech for full hands-free control.`,
      `Many people benefit from a cheatsheet initially.`
    ],
    setupConsiderations: [
      `Type, quality, and location of microphone can impact performance`,
      `Considering combining with other options for mouse control, if possible, so speech is just used for initial text dictation.`,
      `Not all platforms support all features - e.g., not all phones allow answer/end calls by voice.`
    ],

  },
  {
    id: 'partner-observed-action',
    name: 'NO TECH',
    category: AM_CATEGORIES.NO_TECH,
    selectionMethod: [
      LOWTECH_SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.PSCAN
    ],
    imgSrc: imagePath+'no-tech-opt.png',
    imgAlt: 'Partner-observed action',
    subTitle: 'Partner-observed action',
    shortDesc: "Body movement or action that partner can see or hear",
    longDesc: `This is a no-tech method where the user produces a specific signal, using a body movement or vocalization, and a partner notices that and interprets it. Choose a signal that is easy for the user to produce, and easy for the partner to recognize.`,
    textEntryItems: [
        'With partner-assisted scanning and a communication board, the user can select messages or letters to communicate. The partner presents choices from the board, and the user gives the signal when the desired choice comes up.',
    ],
    navigationItems: [
      'A partner-observed action can also be used to signal Yes and No, or a need for help or attention.',
    ],
    motorConsiderations: [
      `Some options to consider: eye movements (up/down, left/right), long eye blinks, furrow brow, purse lips, head movements, open mouth / jaw, thumb, toes, etc.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
      `User and partner will both need to learn the task and work together for success.`
    ],
    setupConsiderations: [
      'Note that you may want to identify a different partner-observed action for each different purpose, if possible.'
    ]
  },
  {
    id: 'button-sw-med',
    name: 'Button switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'button-sw-med-340x240-opt.png',
    imgAlt: 'typical button switch',
    attr: 'Photo by permission of <a href="https://www.ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: 'Jellybean and similar switches',
    shortDesc: 'Typical button switch for hand or other body sites',
    longDesc: `The jellybean and similar switches that are medium size (about 2 1/2" across). Also comes in a larger size of about 5" across. These sturdy switches provide low fuss ways to do a variety of tasks. They require a light press to activate and give auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `The basic button switch can be used with almost any access point, including finger, hand, elbow, shoulder, head, chin, knee.`,
      `The user will need to both activate AND release the switch.`,
    ],
    visualConsiderations: [
      `Some people need to see the switch in order to activate it easily. For others, that doesn't matter.`
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position, consider: hand-held, secured to table / laptray / armrest, mounted in specific spot, strapped to a body part, can be positioned sideways or upside down`,
    ],
  },
  {
    id: 'button-sw-small',
    name: 'Button switch (small)',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'button-sw-sm-520x390-opt.png',
    imgAlt: 'typical smaller button switch',
    attr: 'Photo by permission of <a href="https://www.ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: 'Specs and similar switches',
    shortDesc: 'Typical smaller button switch for hand or other body sites',
    longDesc: `The Specs and similar switches that are smaller size (about 1 3/8" across). These sturdy switches provide low fuss ways to do a variety of tasks. They require a light press to activate and give auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `The smaller switch can be used with almost any access point, including finger, hand, elbow, shoulder, head, chin, knee.`,
      `The user will need to both activate AND release the switch.`,
    ],
    visualConsiderations: [
      `Some people need to see the switch in order to activate it easily. For others, that doesn't matter.`
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position, consider: hand-held, built into splint, secured to table / laptray / armrest, mounted in specific spot, strapped to a body part, can be positioned sideways or upside down`,
    ],
  },
  {
    id: 'microlight',
    name: 'Microlight switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'microlight-480x360-opt.png',
    imgAlt: 'low force switch',
    attr: 'Photo by permission of <a href="https://www.ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: 'Microlight and similar switches',
    shortDesc: 'A switch with lower force requirement, for hand or other body sites',
    longDesc: `The Microlight and similar switches that have a very low force requirement. Can be a good way to capture minimal movement. They require a very light press to activate and give auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Typical access points are finger, head, or chin, using fine motor control`,
      `The user will need to both activate AND release the switch.`,
    ],
    visualConsiderations: [
      `Some people need to see the switch in order to activate it easily. For others, that doesn't matter.`
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position, consider: hand-held, built into splint, secured to table / laptray / armrest, mounted in specific spot, strapped to a body part, can be positioned sideways or upside down`,
    ],
  },
  {
    id: 'thumb-switch',
    name: 'Thumb switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'glassouse-finger-switch-1024x768-opt.png',
    imgAlt: 'thumb switch',
    attr: 'Photo by permission of <a href="https://www.glassouse.com/" target="_blank">Glassouse</a>',
    subTitle: '',
    shortDesc: 'Finger-mounted switch for minimal movement, typically activated with the thumb',
    longDesc: `A very specific switch to capture thumb movement, like starting a stopwatch. They require a very light press to activate and give auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `The main access point is the thumb, using thumb flexion like starting a stopwatch`,
      `The user will need to both activate AND release the switch.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: need to stabilize it in the hand for easier activation`,
    ],
  },
  {
    id: 'plate-switch',
    name: 'Plate switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'pal-pads-1024x768-opt.png',
    imgAlt: 'plate switch',
    attr: 'Photo by permission of <a href="https://www.adaptivation.com/" target="_blank">Adaptivation, Inc.</a>',
    subTitle: '',
    shortDesc: 'Very sensitive switch, for hand or other body sites',
    longDesc: `A flat switch that reduces the 'hurdle' to getting onto the activation surface. Requires a light press to activate. Does not provide its own tactile or auditory feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Typical access points are fingers and hands but can be used with just about any body site`,
    ],
    visualConsiderations: [],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: need to stabilize it in the hand for easier activation`,
    ],
  },
  {
    id: 'wobble-switch',
    name: 'Wobble switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'wobble-switch-ed-1024x768-opt.png',
    imgAlt: 'wobble switch',
    attr: 'Photo by permission of <a href="https://enablingdevices.com/" target="_blank">Enabling Devices</a>',
    subTitle: '',
    shortDesc: 'Can be activated from any direction',
    longDesc: `Can be activated by pushing the wobble stick in any direction. A sturdy switch that provides auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Typical access points are fingers, hand, and head`,
      `Well-suited for either gross or fine motor control`,
      `Can work well with users who have difficulty with a switch that accepts only one type of movement.`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
    ],
  },
  {
    id: 'pillow-switch',
    name: 'Pillow switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'pillow-switch-340x255-opt.png',
    imgAlt: 'pillow switch',
    attr: 'Photo by permission of <a href="https://www.ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: '',
    shortDesc: 'Soft switch for hand or other body sites',
    longDesc: `The pillow and other soft switches provide low fuss ways to do a variety of tasks. They are sturdy and give auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Typical access point is the hand, using grasp / release or pinch / release movements`,
      `Access points such as the head may also be used.`,
      `The user will need to both activate AND release the switch.`,
      `The foam in the switch can help hypertonic hands release the switch due to the foam 'recoil'`,
    ],
    visualConsiderations: [
      `Some people need to see the switch in order to activate it easily. For others, that doesn't matter.`
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position, pillow switch is usually placed in the hand. But can be mounted at another location.`,
    ],
  },
  {
    id: 'grasp-switch',
    name: 'Grasp switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'grasp-switch-400x300-opt.png',
    imgAlt: 'grasp switch',
    attr: 'Photo by permission of <a href="https://www.pretorianuk.com/" target="_blank">Pretorian UK</a>',
    subTitle: '',
    shortDesc: "Typically held in the palm and squeezed to activate",
    longDesc: `A very specific switch to capture a hand squeezing movement. These are sturdy and provide auditory and tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `The main access point is the hand / palm, using a gross grasping squeeze`,
      `Relax the squeeze to release the switch (do not completely let go of the switch).`,
      `Make sure user can comfortably release the switch at rest`
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: needs to stay positioned in the hand`,
    ]
  },
  {
    id: 'bite-switch',
    name: 'Bite switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'bite-switch-600x450-opt.png',
    imgAlt: 'bite switch',
    attr: 'Photo by permission of <a href="https://www.glassouse.com/" target="_blank">Glassouse</a>',
    subTitle: '',
    shortDesc: "Activates by biting on the soft end. Adjust position with bendable cable.",
    longDesc: `The bite switch is a very specific switch that activates by biting on the soft end, then relaxing to release. It provides tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `The bite switch is often used in conjunction with a head-controlled pointing device, to act as a selection switch`,
      `It may also used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `The main access point is the jaw / teeth: activating by biting the soft end`,
      `Release the switch by relaxing the bite (not necessarily fully letting go)`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: generally position between teeth by adjusting the bendable cable`,
      `May need to be removed from mouth to speak or do other things, which may be an issue for some`
    ],
  },
  {
    id: 'sip-puff',
    name: 'Sip and puff switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'sip-puff-280x210-opt.png',
    imgAlt: 'sip and puff switch',
    attr: 'Photo by permission of <a href="https://orin.com/" target="_blank">Origin Instruments</a>',
    subTitle: '',
    shortDesc: 'Control two switches with mouth pressure',
    longDesc: `The sip-puff (or pneumatic) switch provides two switches in one package. Activate one switch by 'sipping' and the other by 'puffing' with the straw. It does not provide direct auditory or tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry that is well-suited for a sip-puff switch. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `The sip-puff switch is often used in conjunction with a head-controlled pointing device, to act as a selection switch`,
      `It may also used for a specific purpose, like turning on a toy or a call light.`,
    ],
    motorConsiderations: [
      `The main access point is the jaw / lips: use lip closure to create a seal, then do a light sip (inward pressure) or a light puff (outward pressure)`,
      `Requires adequate oralmotor skills`,
      `May require some neck movement in order to rotate head to get the straw and release`,
      `Be careful of bite reflexes`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
    ],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: mount so that straw is positioned so user can grab the straw when desired and also release it, usually near the side of the mouth`,
    ],
  },
  {
    id: 'proximity-switch',
    name: 'Proximity switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'proximity-switch-600x450-opt.png',
    imgAlt: 'proximity switch',
    attr: 'Photo by permission of <a href="https://ablenetinc.com/" target="_blank">AbleNet</a>',
    subTitle: '',
    shortDesc: "No force required. Activates when user's body nears the switch top",
    longDesc: `Promximity does not require any touch to activate. It activates when the user's body nears the switch top. These are sturdy and provide auditory feedback, but not tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Can be used with any body part.`,
      `Activates when body part is within 1/2" to 1/4" from switch surface`,
      `Make sure user can comfortably get outside of activation range when resting `
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
      `It can sometimes be difficult for users to learn how close / far they need to be to activate the switch`
    ],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: generally mounted or secured to a fixed point near the body site`,
      `These need batteries, so that requires a bit of monitoring and attention than a typical switch`
    ],
  },
  {
    id: 'tilt-switch',
    name: 'Tilt switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
    ],
    imgSrc: imagePath+'tilt-switch-1024x768-opt.png',
    imgAlt: 'tilt switch',
    attr: 'Photo by permission of <a href="https://www.adaptivation.com/" target="_blank">Adaptivation, Inc.</a>',
    subTitle: '',
    shortDesc: "Tilt-sensitive switch for head, finger, or other site.",
    longDesc: `The tilt switch activates when it is tilted past a certain point from neutral. It does not require any touch to activate and does not provide direct auditory or tactile feedback.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
      `Tilt switches are sometimes used for posture training, to build awareness of head position`
    ],
    motorConsiderations: [
      `Typical access points: head (tilt forward or backward to activate), finger`,
      `But can be used with any body part`,
      `Make sure user can return to neutral position to 'release' the switch`
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [
      `It can sometimes be difficult for users to learn how much tilt is required to activate the switch. May get false activations just through normal movements.`
    ],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `For switch position: generally mounted on a velcro band around the head or other body part`,
    ],
  },
  {
    id: 'other-switches',
    name: 'Other sensor switch',
    category: AM_CATEGORIES.SWITCH_ACCESS,
    selectionMethod: [
      SELECTION_METHODS.DIRECT,
      LOWTECH_SELECTION_METHODS.DIRECT,
      SELECTION_METHODS.SCAN,
      LOWTECH_SELECTION_METHODS.PSCAN,
      SELECTION_METHODS.MORSE
    ],
    imgSrc: imagePath+'other-sensor-switches-1024x768-opt.png',
    imgAlt: 'includes eyeblink, twitch, movement sensor switches generally for minimal movement',
    attr: 'Photo by permission of <a href="https://enablingdevices.com/" target="_blank">Enabling Devices</a>',
    subTitle: 'Twitch, EMG, eyeblink and others',
    shortDesc: 'Group of switches generally for minimal movement',
    longDesc: `These switches may be used less often and are generally for capturing minimal movement. Includes eyeblink, twitch, EMG, movement sensor, or similar switch. These may be more of a challenge to set up and use, but can provide options when no other switch is suitable.`,
    textEntryItems: [
      'For free text entry, use scanning with an on-screen keyboard, with one or two switches.',
      'Morse code is another option for text entry with one or two switches. (See <a href="https://kpronline.com/blog/morse-code-typing-for-the-ipad/" target="_blank">our blog post</a> for a way to do this in iOS.)'
    ],
    navigationItems: [
      'Use for navigation with methods like Switch Control (iOS) or an on-screen keyboard that has mouse emulation.',
      `A switch may be used for a single purpose, like turning on a toy or a call light, or speaking a specific message.`,
    ],
    motorConsiderations: [
      `Eyeblink switch is specialized to use eyeblinks as a switch, generally with 2 slow blinks counting as a switch closure`,
      `Twitch, EMG, and movement sensor switches can be used with basically any muscle / movement`,
    ],
    visualConsiderations: [
    ],
    cognitiveConsiderations: [],
    setupConsiderations: [
      `Check out the switch accessibility features included with your computing platform`,
      `These switches tend to be more challenging to set up and maintain reliable switch activation. We suggest carefully reading product info for details about specific switches.`,
    ],
  },
];

//TODO merge id and value and resolve what properties we actually need
//the properties here are a merge of what we use for Cards (as in TestDriveChooseMethod)
//and what we use for checkboxes (as in EvaluateAbilitiesPhysicalBasic).
//Right now we are using the checkbox-related properties, to see if that's good enough.
//Once we settle a bit more on what we are doing, we could remove unused properties.
export const CONTROL_ACCESSORIES = [ 
  {
    id: 'mount',
    value: 'mount',
    label: 'Mount',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: 'Keeps items in a functional location',
    help:
    `
    <div class="container-fluid">
      <div class="row">
        <p>Mounts can make it easier to find and maintain a good, functional location for items like switches and computing devices.</p>
      </div>
      <div class="row">
        <div class="col-md-3"><img class="aa-modal-image" src="/images/access-methods/modhose-switch-example-2-1200x1200-opt.jpg" /></div>
        <div class="col-md-9">
          <p>For example, this mount holds a switch, which can be velcroed to the circular plate.  The modular hose allows the switch to be moved easily without tools, 
          which may be helpful for finding the best switch location when doingduring assessments.</p>
          <p>If you are considering this type of mount permanently for a particular user, make sure that the switch stays in position during use.</p>
        </div>
      </div>
      <hr />
      <div class="row">
        <div class="col-md-3"><img class="aa-modal-image" src="/images/access-methods/dual-easy-mover-400x334-opt.jpg" /></div>
        <div class="col-md-9">
          <p>This mount holds a smartphone (or other device), and allows you to adjust the tilt and all joints on the mount.</p>
        </div>
      </div>
      <hr />
      <div>
        <p><strong>Considerations for mounting systems include: </strong></p>
        <ol>
          <li>Safety - you don't want the mount to pose a safety risk to the user or those around them. And you want it to protect the device being mounted as well.</li>
          <li>Ease of setup - how will everyone know what the correct position is for the mount? This is especially important if the mount needs to be re-positioned often.</li>  
          <li>Holds position while maintaining flexibility - once it's setup, you want everything to stay in the right place. But you need to ensure that it can move out of the way when needed.</li>
        </ol>
        <p>For more ideas, search the web for "assistive technology mounts" or "assistive technology mounting systems".<p>
        <p>And check out the <a href="https://assistivetechnology.org.uk/mat-doc/" target="_blank">Mounting Assistive Technology Documentation (MAT-DOC)</a> for some best practice guidelines.</p>
      </div>
    </div>    
    `
  },
  {
    id: 'stylus',
    value: 'stylus',
    label: 'Stylus',
    imgSrc: imagePath+'stylus-example-crop-1402x2439-opt.jpg',
    imgAlt: 'stylus',
    subTitle: '',
    shortDesc: 'A stick to press the keys on a keyboard or touchscreen',
    moreInfo: `For pressing keyboard keys or on-screen targets`,
    help: 
    `
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/stylus-example-crop-1402x2439-opt.jpg" /></div>
        <div class="col-md-9">
          <p>A stylus is a stick to help you press the keys on a keyboard or touchscreen. It can be helpful when finger function is limited.</p>
          <p>The photo shows a basic passive stylus that will work with any touchscreen.  These are generally inexpensive and available wherever regular tech stuff is sold
        (e.g., Best Buy). If you are using a regular keyboard, you could also use a pencil with eraser or similar stick, instead of an official stylus.</p>
          <p>For more ideas, search the web for "stylus for accessibility" or "adaptive stylus".</p>
        </div>
      </div>
    </div>
    `
  },
  {
    id: 'universal-cuff',
    value: 'universal-cuff',
    label: 'Universal cuff',
    imgSrc: imagePath+'universal-cuff-example-crop-1908x2065-opt.jpg',
    imgAlt: 'Universal cuff wrapped around hand with stylus placed in the pocket',
    subTitle: '',
    shortDesc: 'The universal cuff holds a stylus for you, which may be easier than holding one in your fingers',
    moreInfo: `Holds a stylus for you`,
    help:
    `
    <div class="container-fluid">
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src="/images/remedies/universal-cuff-example-crop-1908x2065-opt.jpg" /></div>
      <div class="col-md-9">
        <p>The universal cuff wraps around the hand. It has a pocket to hold a stylus or other stick, which may
        be easier than holding one in your hand.</p>
      </div>
    </div>
  </div>

    `
  },
  {
    id: 'splint',
    value: 'splint',
    label: 'Splint',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: 'To provide support and/or protection',
  },
  {
    id: 'mouthstick',
    value: 'mouthstick',
    label: 'Mouthstick',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: `To press keys using head and mouth motion`,
    help:
    `
    <div>
      <p>A mouthstick lets you press keys using head and mouth motion.</p> 
      <ul>
        <li>To press touchscreen targets, attach a capacitive tip to the mouthstick</li>
        <li>Consider the keyboard position as well</li>
        <li>Use a docking station to provide a way to pick up and release the mouthstick</li>
      </ul> 
      <div>
        <p>Here's a good summary from the INDATAProject in Indiana:</p>
        <iframe width="560" height="315" src="https://www.youtube.com/embed/cPs-RdWoVc0" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
      </div>
    </div>
    `
  },
  {
    id: 'docking-station',
    value: 'docking-station',
    label: 'Docking station',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: `To hold styluses or sticks`,
    help:
    `
    <div class="container-fluid">
    <div class="row">
      <div class="col">
        <p>A docking station provides a place to put the stylus (or stick), and holds the stylus in such a way that the user can readily pick it up again. 
        Using a docking station is especially important for mouth stylus users. </p>
        <p>You can try making your own, as in the photos below.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4"><img class="aa-modal-image" src="/images/remedies/early-iteration-mouthstick-docking-station-1-crop-1908x2838-opt.jpg" /></div>
      <div class="col-md-8"><img class="aa-modal-image" src="/images/remedies/custom-stylus-docking-station-1-4032x1908-opt.jpg" /></div>
    </div>
    <br />
    <div class="row">
      <div class="col">
        <p>Do a web search on "mouthstick docking station" for more ideas and some commercially available options.</p>
      </div>
    </div>
  </div>

    `
  },
  {
    id: 'headstick',
    value: 'headstick',
    label: 'Headstick',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: `To press keys using head motion`,
    help:
    `
    <div>
      <p>A headstick attaches to the forehead or chin, allowing direct key presses with head motion.</p> 
      <ul>
        <li>To press touchscreen targets, attach a capacitive tip to the headstick</li>
        <li>Attaching the stick to the chin might reduce interference with vision</li>
        <li>Consider the keyboard position as well</li>
        <li>It may be worth trying a mouthstick as well</li>
      </ul> 
      <div>
        <p>Here's a good summary from the INDATAProject in Indiana:</p>
        <iframe width="560" height="315" src="https://www.youtube.com/embed/cPs-RdWoVc0" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
      </div>
    </div>
    `
  },
  {
    id: 'digit-isolation-glove',
    value: 'digit-isolation-glove',
    label: 'Digit isolation glove',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: 'Reduces unintended contact with touch devices',
    help: 
    `
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-3"><img class="aa-modal-image" src="/images/access-methods/digit-isolation-1724x1138-opt.jpg" /></div>
        <div class="col-md-9">
          <p>The basic idea is to use a glove to reduce the amount of skin that contacts the touchscreen or trackpad. Here are a few options:</p> 
          <ul>
            <li>Cut the index finger tip off of a pair of gloves</li>
            <li>Try gloves with capacitive finger tips made for touchscreen devices</li>
            <li>Try a commercial option like the one in the photo</li>
            <li>Search the web for "digit isolation glove"</li>
          </ul> 
        </div>
      </div>
      <div class="row">
        <div class="col">
        <p><small>Photo by permission of <a href="https://funandfunction.com/?utm_source=app+referral&utm_medium=image&utm_campaign=app_referral_homepage" target="_blank">Fun and Function</a></small></p>
        </div>
      </div>
    </div>
    `  
  },
  {
    id: 'keyguard',
    value: 'keyguard',
    label: 'Keyguard',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: 'To help target individual keys',
    help:
    `
    <div>
    <p>A keyguard attaches to a keyboard and helps reduce typing errors by providing a physical border around each key.</p> 
    <p>Can be very helpful for people who need it.  But if you don't need a keyguard, don't use one, as it will probably slow down your typing in that case. If you're not sure, try it out and see!</p>
    <div>
      <p>Here's a good introduction from the INDATAProject in Indiana:</p>
      <iframe width="560" height="315" src="https://www.youtube.com/embed/zFv2myGVhl8" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>
    </div>
  </div>
`
  },
  {
    id: 'arm-supports',
    value: 'arm-supports',
    label: 'Arm supports',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: 'To allow motion with less strength',
    help:
    `
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-3"><img class="aa-modal-image" src="/images/access-methods/mobile-arm-support-312x162.jpeg" /></div>
        <div class="col-md-9">
          <p>Arm supports can help you hold your arm up and move it against gravity.</p>
          <p>The photo shows a mobile arm support which has a support trough under the forearm and pivot points to aid with motion.</p>
          <p>A simpler arm support could just be a well-positioned desk or lap tray that allows the forearms to rest.</p>
          <p>For more ideas, search the web for "ergonomic arm rest" or "mobile arm support".</p>
        </div>
      </div>
    </div>    
    `
  },
  {
    id: OTHER_CKBX_VAL,
    value: OTHER_CKBX_VAL,
    label: 'Other',
    imgSrc: imagePath+'',
    imgAlt: '',
    subTitle: '',
    shortDesc: '',
    moreInfo: '(specify)',
  },
]

export function getAccessMethodNameForID(id){
  const allItems = ACCESS_METHODS;
  const matchingItem = allItems.find(item => (id === item.id));
  if (matchingItem)
    return matchingItem.name;
  else
    return ""; //no match found
}
export function getAccessMethodCategoryForID(id){
  const allItems = ACCESS_METHODS;
  const matchingItem = allItems.find(item => (id === item.id));
  if (matchingItem)
    return matchingItem.category;
  else
    return ""; //no match found
}
export function getAccessMethodSelectionMethodsForID(id){
  const allItems = ACCESS_METHODS;
  const matchingItem = allItems.find(item => (id === item.id));
  if (matchingItem && matchingItem.selectionMethod)
    return matchingItem.selectionMethod;
  else
    return []; //no match found
}
//a wrapper function for returning the access methods for a given category
//returns empty array if no match
export function getMethodsForCategory(cat){
  return(ACCESS_METHODS.filter(method => (method.category === cat)));
}
